import React from 'react';
import PropTypes from 'prop-types';
import { FormControl, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, TextField } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';
import { dateUtcToS } from '../../../util/dateUtil';
import { CAREER_OPTIONS, TRANSPORTATION_OPTIONS } from '../../../constants/general';

const WorkforcePlanV2 = ({ form, setForm, classes, disabled }) => {
  return (
    <>
      <Grid container direction="row" alignItems="center" className={classes.unprintedContent}>
        <Grid item xs={12} md={3} className={classes.textRowLabelSpacer}>
          <label>Are you currently employed?</label>
        </Grid>
        <Grid item xs={12} md={6} className={classes.rowSpacer} style={{ marginTop: 8, marginBottom: 8 }}>
          <FormControl fullWidth>
            <RadioGroup
              row
              aria-label="Currently employed"
              name="workForceCurrentlyEmployed"
              value={form.workForceCurrentlyEmployed}
              onChange={(event) => {
                if (disabled) return;
                setForm({ ...form, workForceCurrentlyEmployed: event.target.value });
              }}
              disabled={disabled}
            >
              <FormControlLabel
                value="yes"
                control={<Radio color="default" disabled={disabled} />}
                label={<span className={classes.radioLabel}>Yes</span>}
              />
              <FormControlLabel
                value="no"
                control={<Radio color="default" disabled={disabled} />}
                label={<span className={classes.radioLabel}>No</span>}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.printedContent}>
        <Grid item xs={12} md={3} className={classes.textRowLabelSpacer}>
          <label>Are you currently employed?</label>
        </Grid>
        <Grid item xs={false} md={3} lg={false} />
        <Grid item xs={6} md={6} className={classes.rowSpacer}>
          {form.workForceCurrentlyEmployed}
        </Grid>
      </Grid>

      {/* Conditional rendering for employment status */}
      {form.workForceCurrentlyEmployed === 'no' && (
        <>
          <Grid container direction="row" className={classes.unprintedContent} style={{ marginTop: 15 }}>
            <Grid item xs={12} sm={3} className={classes.textRowLabelSpacer}>
              <label id="potential-employers-label" htmlFor="workForcePotentialEmployers">
                Potential Places of Employment:
              </label>
            </Grid>
            <Grid item xs={1} sm={true} />
            <Grid item xs={11} sm={9} className={classes.rowSpacer50}>
              <FormControl fullWidth>
                <TextField
                  id="workForcePotentialEmployers"
                  aria-labelledby="potential-employers-label"
                  size="small"
                  hiddenLabel
                  variant="outlined"
                  value={form.workForcePotentialEmployers}
                  onChange={(event) => {
                    if (disabled) return;
                    setForm({ ...form, workForcePotentialEmployers: event.target.value });
                  }}
                  inputProps={{ maxLength: 255, classes: { input: classes.textField } }}
                  disabled={disabled}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container direction="row" className={classes.printedContent}>
            <Grid item xs={3}>
              <span className="printed-label">Potential Places of Employment:</span>
            </Grid>
            <Grid item xs={9}>
              {form.workForcePotentialEmployers}
            </Grid>
          </Grid>
        </>
      )}
      {form.workForceCurrentlyEmployed === 'yes' && (
        <>
          <Grid container direction="row" className={classes.unprintedContent}>
            <Grid item xs={1} sm={true} />
            <Grid item xs={12} sm={2} className={classes.textRowLabelSpacer}>
              <label id="employer-name-label" htmlFor="workForceEmployerName">
                Employer Name
              </label>
            </Grid>
            <Grid item xs={11} sm={9} className={classes.rowSpacer50} style={{ marginTop: 8, marginBottom: 8 }}>
              <FormControl fullWidth>
                <TextField
                  id="workForceEmployerName"
                  aria-labelledby="employer-name-label"
                  size="small"
                  hiddenLabel
                  variant="outlined"
                  value={form.workForceEmployerName}
                  onChange={(event) => {
                    if (disabled) return;
                    setForm({ ...form, workForceEmployerName: event.target.value });
                  }}
                  inputProps={{ maxLength: 255, classes: { input: classes.textField } }}
                  disabled={disabled}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container direction="row" className={classes.printedContent}>
            <Grid item xs={3}>
              <label>Employer Name</label>
            </Grid>
            <Grid item xs={9}>
              {form.workForceEmployerName}
            </Grid>
          </Grid>

          <Grid container direction="row" className={classes.unprintedContent}>
            <Grid item xs={1} sm={true} />
            <Grid item xs={12} sm={2} className={classes.textRowLabelSpacer}>
              <label id="start-date-label" htmlFor="workForceStartDate">
                Start Date
              </label>
            </Grid>
            <Grid item xs={11} sm={9} className={classes.rowSpacer50} style={{ marginTop: 8, marginBottom: 8 }}>
              {!disabled ? (
                <MuiPickersUtilsProvider utils={LuxonUtils}>
                  <KeyboardDatePicker
                    id="workForceStartDate"
                    aria-labelledby="start-date-label"
                    autoOk
                    variant="inline"
                    disableFuture
                    hiddenLabel
                    value={form.workForceStartDate || null}
                    onChange={(date) => {
                      if (disabled) return;
                      setForm({ ...form, workForceStartDate: date ? date : null });
                    }}
                    format="MM/dd/yyyy"
                    className={classes.datepicker}
                    disabled={disabled}
                    style={{ width: '23%' }}
                  />
                </MuiPickersUtilsProvider>
              ) : (
                <summary style={{ marginTop: 3 }} className={classes.subtleBold}>
                  {dateUtcToS(form.workForceStartDate || null)}
                </summary>
              )}
            </Grid>
          </Grid>
          <Grid container direction="row" className={classes.printedContent}>
            <Grid item xs={3}>
              <label>Start Date</label>
            </Grid>
            <Grid item xs={9}>
              {dateUtcToS(form.workForceStartDate || null)}
            </Grid>
          </Grid>

          <Grid container direction="row" className={classes.unprintedContent}>
            <Grid item xs={1} sm={true} />
            <Grid item xs={12} sm={2} className={classes.textRowLabelSpacer}>
              <label id="position-label" htmlFor="workForcePosition">
                Position
              </label>
            </Grid>
            <Grid item xs={11} sm={9} className={classes.rowSpacer50} style={{ marginTop: 8, marginBottom: 8 }}>
              <FormControl fullWidth>
                <TextField
                  id="workForcePosition"
                  aria-labelledby="position-label"
                  size="small"
                  hiddenLabel
                  variant="outlined"
                  value={form.workForcePosition}
                  onChange={(event) => {
                    if (disabled) return;
                    setForm({ ...form, workForcePosition: event.target.value });
                  }}
                  inputProps={{ maxLength: 255, classes: { input: classes.textField } }}
                  disabled={disabled}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container direction="row" className={classes.printedContent}>
            <Grid item xs={3}>
              <label>Position</label>
            </Grid>
            <Grid item xs={9}>
              {form.workForcePosition}
            </Grid>
          </Grid>
        </>
      )}

      <Grid container direction="row" alignItems="center" className={classes.unprintedContent}>
        <Grid item xs={12} md={3} className={classes.textRowLabelSpacer}>
          <label>Position/Career Field:</label>
        </Grid>
        <Grid item xs={12} md={4} className={classes.rowSpacer} style={{ marginTop: 8, marginBottom: 8 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              id="workForcePositionSelect"
              value={form.workForcePositionOrCareer}
              onChange={(event) => {
                if (disabled) return;
                setForm({ ...form, workForcePositionOrCareer: event.target.value });
              }}
              disabled={disabled}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select</em>
              </MenuItem>
              {CAREER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.printedContent}>
        <Grid item xs={6} sm={2} className={classes.textRowLabelSpacer}>
          <label>Position/Career Field:</label>
        </Grid>
        <Grid item xs={6} sm={4} className={classes.rowSpacer}>
          {form.workForcePositionOrCareer}
        </Grid>
      </Grid>

      {form.workForcePositionOrCareer === 'other' && (
        <>
          <Grid container direction="row" className={classes.unprintedContent} style={{ marginTop: 10 }}>
            <Grid item xs={1} sm={false} lg={false} />
            <Grid item xs={12} sm={2} className={classes.textRowLabelSpacer}>
              <label id="please-describe-label" htmlFor="workForcePositionOther">
                Please describe:
              </label>
            </Grid>
            <Grid item xs={11} sm={9} className={classes.rowSpacer50}>
              <FormControl fullWidth>
                <TextField
                  id="workForcePositionOther"
                  aria-labelledby="please-describe-label"
                  size="small"
                  hiddenLabel
                  variant="outlined"
                  value={form.workForcePositionOther}
                  onChange={(event) => {
                    if (disabled) return;
                    setForm({ ...form, workForcePositionOther: event.target.value });
                  }}
                  inputProps={{ maxLength: 255, classes: { input: classes.textField } }}
                  disabled={disabled}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container direction="row" className={classes.printedContent}>
            <Grid item xs={3}>
              <label>Please describe:</label>
            </Grid>
            <Grid item xs={9}>
              {form.workForcePositionOther}
            </Grid>
          </Grid>
        </>
      )}

      <Grid container direction="row" className={classes.unprintedContent} style={{ marginTop: 10 }}>
        <Grid item xs={12} sm={3} className={classes.textRowLabelSpacer}>
          <label id="next-step-label" htmlFor="workForceNextStep">
            Next steps to complete my plan:
          </label>
        </Grid>
        <Grid item xs={1} sm={true} />
        <Grid item xs={11} sm={9} className={classes.rowSpacer50} style={{ marginTop: 8, marginBottom: 8 }}>
          <FormControl fullWidth>
            <TextField
              id="workForceNextStep"
              aria-labelledby="next-step-label"
              size="small"
              hiddenLabel
              variant="outlined"
              value={form.workForceNextStep}
              onChange={(event) => {
                if (disabled) return;
                setForm({ ...form, workForceNextStep: event.target.value });
              }}
              inputProps={{ maxLength: 255, classes: { input: classes.textField } }}
              disabled={disabled}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.printedContent}>
        <Grid item xs={3}>
          <label>Next steps to complete my plan</label>
        </Grid>
        <Grid item xs={9}>
          {form.workForceNextStep}
        </Grid>
      </Grid>

      <Grid container direction="row" className={classes.unprintedContent}>
        <Grid item xs={12} sm={3} className={classes.textRowLabelSpacer}>
          <label id="transportation-plan-label" htmlFor="workForceTransportation">
            Transportation Plan
          </label>
        </Grid>
        <Grid item xs={12} md={4} className={classes.rowSpacer}>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              id="workForceTransportation"
              aria-labelledby="transportation-plan-label"
              value={form.workForceTransportation}
              onChange={(event) => {
                if (disabled) return;
                setForm({ ...form, workForceTransportation: event.target.value });
              }}
              disabled={disabled}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select</em>
              </MenuItem>
              {TRANSPORTATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.printedContent}>
        <Grid item xs={3}>
          <label>Transportation Plan</label>
        </Grid>
        <Grid item xs={9}>
          {form.workForceTransportation}
        </Grid>
      </Grid>
    </>
  );
};

WorkforcePlanV2.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
};

export default WorkforcePlanV2;
